import { z } from 'zod';

/**
 * Storage ("Guardado") response contracts — NEX-1151, the client-facing side of the storage
 * feature. Not invented — read directly off nexvia-api-web's real source on this same branch
 * (a sibling repo, read-only reference per AGENTS.md), not just the handoff doc this task was
 * given:
 *
 *  - `StorageRequestSchema` matches `storage-request.repository.ts`'s `toPlain()` exactly
 *    (NEX-1167 added `acceptedQuoteId` and `destination*`) —
 *    the function behind `findByClientId` (`GET /storage/requests/mine`), `findById` (the
 *    `request` half of `GET /storage/requests/:id/quotes`) and `save` (`POST /storage/requests`
 *    's response) alike, so one schema is correct for all three.
 *  - `StorageQuoteSchema` matches `storage-quote.repository.ts`'s `findByRequestId` exactly —
 *    the function behind `GET /storage/requests/:id/quotes`'s `quotes` array. That function
 *    deliberately does NOT select `service_fee_percent`/`payment_fee_percent` (Nexvia's own
 *    commission, never shown to the client — same disclosure rule the shipment quotes already
 *    follow), unlike this module's other repository methods (`findById`, `findByProfileId`),
 *    which do — do not add those two fields here on the strength of the entity alone.
 *  - `StoragePaymentStatusSchema` matches `get-storage-payment-status.handler.ts`'s answers: since
 *    NEX-1169 (plan 1.4) a union by `modality` — the monthly subscription's state, or the pago
 *    único's order in its two branches (the active-order branch and the no-active-but-recent-order
 *    fallback branch). `expiresAt` is the one field the fallback branch omits outright (not `null` —
 *    genuinely absent from the object literal, so JSON drops the key), hence `.optional()` on top of
 *    `.nullable()` there.
 *
 * Every endpoint below answers inside a `{ success: true, data: ... }` envelope (confirmed
 * against all five handlers) — unlike this app's legacy `/shipments` and `/quotes` endpoints,
 * which respond with the bare object. `features/storage/queries.ts` parses `res.data.data`
 * (or `res.data.data.requests`), never `res.data` directly.
 *
 * Money/percent fields (`volumeM3`, `monthlyPrice`, `fullPeriodPrice`, `ivaPercent`,
 * `monthlyClientAmount`, `fullPeriodClientAmount`, payment-status `amount`/`displayAmount`) are
 * all `decimal` columns, but every one of them is explicitly `Number(...)`-cast server-side
 * before the response leaves the backend (`storage-request.repository.ts`'s `toPlain`,
 * `storage-quote.repository.ts`'s `findByRequestId`, `get-storage-payment-status.handler.ts`) —
 * same pattern `contracts/shipment.ts` documents for its own parseFloat'd fields, and the
 * opposite of `contracts/quote.ts`'s `amount`/`clientAmount` (which the backend does NOT cast,
 * hence `z.coerce.number()` there). Plain `z.number()` is correct here, not `z.coerce.number()`.
 */

// ── Enums ──

export const StorageSpaceTypeSchema = z.enum(['box_chico', 'box_mediano', 'box_grande', 'personalizado']);
// NEX-1166 (decision A9) — a code of the catalog's kind 'storage_type' (GET /storage/service-options
// ?kind=storage_type): 'privado', 'compartido' and 'otro' are seeded and Nexvia can add more, so a
// fixed enum here would make .parse reject every list holding a newer code.
export const StorageTypeSchema = z.string().min(1);

// storage.constants.ts's REQUEST_STATUS — only 'published' is written by this milestone's
// handlers, but all four are real, reachable values of the migration's own CHECK constraint.
export const StorageRequestStatusSchema = z.enum(['published', 'accepted', 'cancelled', 'expired']);

// storage.constants.ts's QUOTE_STATUS.
export const StorageQuoteStatusSchema = z.enum(['pending', 'accepted', 'rejected', 'expired', 'cancelled']);

// storage-payment.constants.ts's STORAGE_PAYMENT_ORDER_STATUS.
export const StoragePaymentOrderStatusSchema = z.enum([
  'pending_payment',
  'pending_accreditation',
  'paid',
  'rejected',
  'expired',
  'cancelled'
]);

// NEX-1170 — storage.constants.ts's STORAGE_STATUS: the guardado's lifecycle once a payment accepted a
// quote (nexvia-api-web domain/storage-lifecycle.ts). `null` while the request is not contracted.
export const StorageGuardadoStatusSchema = z.enum(['contracted', 'active', 'ending', 'ended']);

// NEX-1170 — how the accepted quote was paid: pago único ("one_time") or the monthly subscription.
export const StoragePaymentModalitySchema = z.enum(['one_time', 'subscription']);

// NEX-1170 — who confirmed the depósito received the things (decision A4): the depósito from its panel
// or Nexvia's support from the backoffice. NEX-1168 (plan 2026-10-20 Tarea 2.5, contract P2.3) — or
// `delivery_code`: a shipment straight to the depósito delivered with its delivery code confirms the
// reception by itself (the API's `reception_confirmed_by`).
export const StorageReceptionConfirmedBySchema = z.enum(['storage', 'backoffice', 'delivery_code']);

// NEX-1170 (X1) — who ended the guardado: the depósito ("El cliente retiró sus cosas"), the client
// ("Retiré por mi cuenta") or the API's daily job, once a cancelled guardado's end passed.
export const StorageEndedBySchema = z.enum(['client', 'storage', 'system']);

/**
 * NEX-1170 — `domain/guardado-view.ts`'s lifecycle fields (nexvia-api-web, same branch), also part of
 * `toPlain` (so of every request read) plus `paymentModality` on `GET /storage/requests/mine`. All
 * `null` until a payment accepts a quote, and `.optional()`: an API without the columns omits them.
 * `endsAt` is a calendar day ("YYYY-MM-DD"); the other dates are instants. The depósito's "No lo
 * recibí" reason (`notReceivedReason`) never reaches the client: only its date.
 */
const StorageGuardadoLifecycleShape = {
  storageStatus: StorageGuardadoStatusSchema.nullable().optional(),
  paymentModality: StoragePaymentModalitySchema.nullable().optional(),
  receivedAt: z.string().nullable().optional(),
  receptionNote: z.string().nullable().optional(),
  receptionConfirmedBy: StorageReceptionConfirmedBySchema.nullable().optional(),
  notReceivedAt: z.string().nullable().optional(),
  endsAt: z.string().nullable().optional(),
  cancelRequestedAt: z.string().nullable().optional(),
  endedAt: z.string().nullable().optional(),
  // NEX-1170 (X1) — absent from an API before X1, like the rest.
  endedBy: StorageEndedBySchema.nullable().optional(),
  withdrawalDraftId: z.union([z.number(), z.string()]).nullable().optional(),
  withdrawalRequestedAt: z.string().nullable().optional()
};

/**
 * NEX-1170 (audit C4/W3) — on each row of `GET /storage/requests/mine`, only while the guardado is
 * `contracted` or `active` (else `null`): the depósito's notice in days and the end date the baja would
 * get if the client asked for it today ("YYYY-MM-DD", the same rule the API applies on
 * `POST .../cancel`), so the confirmation can show it before confirming. `.optional()`: an API without
 * them omits them, and the modal then says the date is calculated on confirming.
 */
const StorageCancellationPreviewShape = {
  noticeDays: z.number().nullable().optional(),
  estimatedEndsAtIfCancelledToday: z.string().nullable().optional()
};

// ── Guardado reviews (NEX-1329, decision A6) ──

/**
 * NEX-1329 — one review of a guardado as its parties see it (nexvia-api-web
 * `domain/guardado-review.ts`'s `reviewView`): stars 1-5 and an optional comment (`null` when none).
 */
export const StorageReviewSchema = z.object({
  id: z.union([z.number(), z.string()]),
  rating: z.number(),
  comment: z.string().nullable().optional(),
  createdAt: z.string()
});

/**
 * NEX-1329 — the client's side of the guardado's reviews on each row of `GET /storage/requests/mine`
 * (get-storage-requests-mine.handler.ts): `canReview` from the reception until the client rates the
 * depósito, `myReview` (what the client wrote) and `theirReview` (what the depósito wrote about the
 * client). `.nullable().optional()`: an API without NEX-1329 omits them.
 */
const StorageReviewStateShape = {
  canReview: z.boolean().nullable().optional(),
  myReview: StorageReviewSchema.nullable().optional(),
  theirReview: StorageReviewSchema.nullable().optional()
};

/** NEX-1329 — `POST /storage/requests/:id/reviews`'s `data` (guardado-review.handler.ts's `submit`). */
export const StorageSubmitReviewResultSchema = z.object({
  review: StorageReviewSchema.extend({ reviewType: z.string() })
});

// ── Storage request ──

export const StorageRequestSchema = z.object({
  id: z.union([z.number(), z.string()]),
  clientId: z.union([z.number(), z.string()]),
  spaceType: StorageSpaceTypeSchema,
  volumeM3: z.number().nullable(),
  storageType: StorageTypeSchema,
  department: z.string(),
  locality: z.string(),
  periodStart: z.string(),
  periodMonths: z.number().nullable(),
  openEnded: z.boolean(),
  photos: z.array(z.string()),
  neededOptions: z.array(z.string()),
  needsTransport: z.boolean(),
  // NEX-1166 — "sí, cotizarlo con Nexvia" (true) vs "lo resuelvo yo" (false). An API without the
  // column does not send it; read that as false, the column's own default.
  transportQuotedByNexvia: z.boolean().optional().default(false),
  // NEX-1167 — `toPlain`'s acceptance fields: the quote the client's payment accepted and that
  // quote's location COPIED as the destination (business doc §6: "la dirección del depósito queda
  // como destino"). All `null` while the request is published, and the destination stays `null`
  // for a quote with no location. Optional too: an API without the columns omits them.
  acceptedQuoteId: z.union([z.number(), z.string()]).nullable().optional(),
  destinationAddress: z.string().nullable().optional(),
  destinationDepartment: z.string().nullable().optional(),
  destinationLocality: z.string().nullable().optional(),
  // NEX-1168 (plan 2026-10-20, Tarea 2.6; nexvia-api-web contract §5) — only on `GET /storage/requests/mine`: the
  // coordinates of the accepted quote's location, read live (`null` while not accepted, or for a location without
  // them), for "Cotizar el traslado" (contracts/shipment-wizard-prefill.ts). A value that is not a number reads as
  // `null`: the wizard then asks to pick the address, as before.
  destinationLatitude: z.number().nullable().optional().catch(null),
  destinationLongitude: z.number().nullable().optional().catch(null),
  destinationPlaceId: z.string().nullable().optional().catch(null),
  // NEX-1168 (contract §4) — what goes to the depósito, for the carrier: the calculator's counts (keys of
  // `STORAGE_ITEM_TYPES`, zeros dropped; `null` with none) and the client's note («¿Algo que el transportista deba
  // saber…?», ≤ 500). The carrier reads them on the shipment's stop; this web only sends them, so a value it
  // cannot read is `null` rather than a failed request.
  calculatorItems: z.record(z.string(), z.number()).nullable().optional().catch(null),
  transportNote: z.string().nullable().optional().catch(null),
  status: StorageRequestStatusSchema,
  ...StorageGuardadoLifecycleShape,
  ...StorageReviewStateShape,
  ...StorageCancellationPreviewShape,
  createdAt: z.string(),
  updatedAt: z.string()
});

// ── Guardado lifecycle actions (NEX-1170, client-guardado.handler.ts) ──

/** `data.request` of the client's lifecycle endpoints: `guardadoView`, not the whole request. */
export const StorageGuardadoViewSchema = z.object({
  id: z.union([z.number(), z.string()]),
  status: StorageRequestStatusSchema,
  ...StorageGuardadoLifecycleShape
});

/**
 * `POST /storage/requests/:id/cancel` and `.../self-withdrawal`. `subscriptionCancellation`: since
 * NEX-1169 (plan 2026-10-20 task 1.5) a monthly guardado's Mercado Pago subscription is cancelled for
 * real, so it is `null`; "manual_pending" only when Mercado Pago failed and Nexvia cancels it by hand.
 */
export const StorageGuardadoActionResultSchema = z.object({
  request: StorageGuardadoViewSchema,
  subscriptionCancellation: z.enum(['manual_pending']).nullable().optional()
});

/**
 * NEX-1169 (plan 2026-10-20 task 1.5) — `POST /storage/requests/:id/subscription/cancel` ("Cancelar el cobro
 * mensual", client-guardado.handler.ts's `cancelSubscription`): the guardado (now `ending`, `endsAt` = the last
 * day charged; an `ending` one keeps its end), the subscription as it stands (`status` stays live when Mercado
 * Pago failed; `cancelledAt` an ISO instant) and `subscriptionCancellation` ("manual_pending" only on that
 * failure). `status` is not narrowed and everything but `request` is optional: a tolerant read.
 */
export const StorageSubscriptionCancelResultSchema = z.object({
  request: StorageGuardadoViewSchema,
  subscription: z
    .object({
      status: z.string(),
      cancelledAt: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  subscriptionCancellation: z.enum(['manual_pending']).nullable().optional()
});

/**
 * NEX-1169 (plan 2026-10-20 task 1.8) — `POST /storage/requests/:id/renew { months }` (storage-renewal-checkout.ts):
 * a NEW pago único checkout for `months` more months. `periodStart` is the day after the current `endsAt` and
 * `periodEnd` the last day included ("YYYY-MM-DD"); `amount` in minor units, `displayAmount` its decimal (the
 * accepted quote's monthly price × months); `expiresAt` an ISO instant (2 h); `resumed` — the same renewal was
 * already open and is answered again. Only what the panel shows is required.
 */
export const StorageRenewalResultSchema = z.object({
  paymentOrderId: z.union([z.number(), z.string()]).optional(),
  checkoutUrl: z.string().nullable().optional(),
  sandboxCheckoutUrl: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  months: z.number().optional(),
  periodStart: z.string(),
  periodEnd: z.string(),
  amount: z.number().optional(),
  displayAmount: z.number(),
  currency: z.string().optional().default('UYU'),
  resumed: z.boolean().optional().default(false)
});

/**
 * NEX-1169 (plan 2026-10-20 task 1.8) — the `data` of the renewal's 409 PAYMENT_ALREADY_IN_PROGRESS: the
 * renewal that still waits for its payment (its Mercado Pago link and months). Every field optional.
 */
export const StorageRenewalInProgressSchema = z.object({
  existingPaymentOrderId: z.union([z.number(), z.string()]).nullable().optional(),
  checkoutUrl: z.string().nullable().optional(),
  renewalMonths: z.number().nullable().optional()
});

/**
 * `POST /storage/requests/:id/withdrawal` — the shipment DRAFT (`shipment_drafts`) from the depósito to
 * the destination the client gave; the wizard opens it by its id (GET /shipments/drafts/:id). `created:
 * false` when a draft of an earlier request still existed (it is answered again).
 */
export const StorageWithdrawalResultSchema = z.object({
  created: z.boolean(),
  request: StorageGuardadoViewSchema,
  draft: z.object({
    id: z.union([z.number(), z.string()]),
    clientId: z.union([z.number(), z.string()]).nullable().optional(),
    data: z.record(z.string(), z.unknown()).nullable().optional()
  })
});

// ── Storage quote ──

// NEX-1329 — the depósito's reputation next to its name (get-storage-request-quotes.handler.ts): the
// average of its clients' reviews with one decimal (`null` without reviews) and how many there are.
// `.nullable().optional()`: an API without NEX-1329 sends only the name.
export const StorageQuoteDepositSchema = z.object({
  businessName: z.string(),
  averageRating: z.number().nullable().optional(),
  reviewCount: z.number().nullable().optional(),
  // NEX-1329 (audit C2) — the depósito whose reviews `GET /storage/depots/:id/reviews` answers. The
  // quote's own `storageProfileId` carries the same value, so an API without it still works.
  storageProfileId: z.union([z.number(), z.string()]).nullable().optional()
});

/**
 * NEX-1329 (audit C2/W5) — `GET /storage/depots/:storageProfileId/reviews`'s `data`: the depósito's
 * average from its clients (one decimal, `null` without reviews), how many there are and the latest
 * ones — only `client_to_storage`, with no data of the client who wrote them.
 */
export const StorageDepotReviewsSchema = z.object({
  averageRating: z.number().nullable().optional(),
  reviewCount: z.number().nullable().optional(),
  reviews: z.array(StorageReviewSchema).optional().default([])
});

// NEX-1165 — `storage-quote.repository.ts`'s `toQuoteLocation`: the depósito location the quote
// was sent from, with its public (unsigned) photo URLs — the ones the client sees next to the
// quote. `storage_locations.id` is a SERIAL, so pg returns a plain number. The whole object is
// `null` (see `location` below) when the quote predates NEX-1165 or its location was deleted
// (`FK_storage_quotes_location` is `ON DELETE SET NULL`). `id` is optional on purpose: no screen
// reads it (the quote's own `storageLocationId` carries the same value) and an API without
// `toQuoteLocation` omits it, so requiring it would make `.parse` reject the whole quote list.
export const StorageQuoteLocationSchema = z.object({
  id: z.number().optional(),
  department: z.string(),
  locality: z.string(),
  address: z.string(),
  photos: z.array(z.string())
});

const StorageQuoteServiceLabelSchema = z.object({
  code: z.string(),
  label: z.string()
});

// Deliberately has NO serviceFeePercent/paymentFeePercent field — see header. Do not add them.
export const StorageQuoteSchema = z.object({
  id: z.union([z.number(), z.string()]),
  storageProfileId: z.union([z.number(), z.string()]),
  storageLocationId: z.number().nullable(),
  monthlyPrice: z.number(),
  // Unlike fullPeriodClientAmount below, this column has no `nullable: true` in the entity and
  // the repository casts it unconditionally (`Number(row.full_period_price)`, no null-guard) —
  // confirmed via storage-quote.entity.ts + storage-quote.repository.ts, not assumed from the
  // task's own type list alone (which also gives it no `| null`).
  fullPeriodPrice: z.number(),
  currency: z.string(),
  includedServices: z.array(z.string()),
  // NEX-1166 — `storage-quote.repository.ts`'s INCLUDED_SERVICE_LABELS_COLUMN: each
  // included service with its catalog label, in `includedServices`' order, inactive options included
  // (a value with no catalog row is labeled with itself). An API without the field reads as [].
  includedServiceLabels: z.array(StorageQuoteServiceLabelSchema).optional().default([]),
  conditions: z.string().nullable(),
  transportIncluded: z.boolean(),
  // NEX-1167 — an ISO instant in UTC: 23:59:59.999 of the last valid day in
  // America/Montevideo ('2026-12-31' -> '2027-01-01T02:59:59.999Z'). Shown with
  // utils/dateFormat.ts's formatDateInMontevideo, never by its UTC date part.
  validUntil: z.string().nullable(),
  status: StorageQuoteStatusSchema,
  ivaPercent: z.number(),
  monthlyClientAmount: z.number(),
  fullPeriodClientAmount: z.number().nullable(),
  createdAt: z.string(),
  acceptedAt: z.string().nullable(),
  deposit: StorageQuoteDepositSchema,
  location: StorageQuoteLocationSchema.nullable()
});

// ── Service-options catalog (GET /storage/service-options) ──

// NEX-1166 — one active row of `storage_service_options` (storage-service-option.entity.ts). `kind`
// is 'service' (what a depósito offers and a client asks for and filters by), 'condition' (what a
// depósito imposes) or 'storage_type' (the client's single choice in the request). Kept as a plain
// string: the backend owns that list, and the screens only compare it against the kinds they ask for.
export const StorageServiceOptionSchema = z.object({
  id: z.union([z.number(), z.string()]),
  code: z.string().min(1),
  label: z.string(),
  category: z.string().nullable().optional(),
  kind: z.string(),
  displayOrder: z.number().optional()
});

export const StorageServiceOptionListSchema = z.array(StorageServiceOptionSchema);

// ── Request photo upload (POST /storage/requests/photos/upload) ──

// NEX-1166 — one photo of what the client wants to store, put in Spaces by the API
// (request-storage-request-photo-upload-url.handler.ts, 2026-10-28): the KEY the request sends back
// in `photos[]`.
export const StorageRequestPhotoUploadSchema = z.object({
  photoKey: z.string().min(1)
});

// ── Payment status (GET /storage/payment/status/:storageRequestId) ──

// NEX-1169 (plan 1.4) — the endpoint answers either modality (get-storage-payment-status.handler.ts):
// the pago único's order (`modality: 'one_time'`) or the monthly subscription's state
// (`modality: 'subscription'`), `null` when the request has neither. Which one wins when both exist is
// the API's call (an order still open > a live subscription > the most recent of the two).

// The pago único's order. `modality` arrived with plan 1.4: optional, so an API from before it still parses.
export const StorageOneTimePaymentStatusSchema = z.object({
  modality: z.literal('one_time').optional(),
  paymentOrderId: z.union([z.number(), z.string()]),
  status: StoragePaymentOrderStatusSchema,
  checkoutUrl: z.string().nullable(),
  amount: z.number(),
  displayAmount: z.number(),
  currency: z.string(),
  // Absent entirely (not `null`) on the handler's no-active-but-recent-order fallback branch —
  // see header.
  expiresAt: z.string().nullable().optional(),
  remainingSeconds: z.number().nullable(),
  storageQuoteId: z.union([z.number(), z.string()]),
  storageRequestId: z.union([z.number(), z.string()]),
  // NEX-1169 (plan 2026-10-20 task 1.8) — a renewal is a pago único order too: `renewalMonths` is its months
  // (`null` for the contract's own order) and `periodStart`/`periodEnd` the period it pays ("YYYY-MM-DD", the
  // last day included; `null` on orders older than the period). Absent from an API before 1.8.
  renewalMonths: z.number().nullable().optional(),
  periodStart: z.string().nullable().optional(),
  periodEnd: z.string().nullable().optional()
});

// NEX-1169 (plan 1.4) — storage-payment.constants.ts's STORAGE_SUBSCRIPTION_STATUS plus `past_due` (plan
// 1.6, a monthly charge Mercado Pago could not make): the values the table's CHECK allows.
export const StorageSubscriptionStatusSchema = z.enum(['pending', 'authorized', 'paused', 'cancelled', 'past_due']);

// NEX-1169 (plan 1.4) — the subscription's latest charge. `status` is Mercado Pago's own invoice vocabulary
// ('approved', 'rejected'…), stored as it comes, so it is not narrowed here; `chargedAt` is an ISO instant
// and `amountMinor` is in minor units (nexvia-api-web Money).
export const StorageSubscriptionChargeSchema = z.object({
  status: z.string(),
  chargedAt: z.string(),
  amountMinor: z.number()
});

// NEX-1169 (plan 1.4) — the monthly subscription. `nextPaymentDate` is a calendar day ('YYYY-MM-DD'),
// `null` until Mercado Pago sends one; amounts in minor units.
export const StorageSubscriptionPaymentStatusSchema = z.object({
  modality: z.literal('subscription'),
  status: StorageSubscriptionStatusSchema,
  nextPaymentDate: z.string().nullable().optional(),
  autoRenew: z.boolean().default(false),
  monthlyAmountMinor: z.number(),
  currency: z.string(),
  lastCharge: StorageSubscriptionChargeSchema.nullable().optional(),
  // NEX-1169 (plan 2026-10-20 tasks 1.5/1.6, additive) — only while `past_due`: when the first charge failed
  // (ISO instant) and the last day of the grace ('YYYY-MM-DD', after it the monthly charge is cancelled and the
  // depósito given the baja); when it was cancelled (ISO instant). Absent from an API before them.
  pastDueSince: z.string().nullable().optional(),
  graceEndsAt: z.string().nullable().optional(),
  cancelledAt: z.string().nullable().optional()
});

// The subscription first: it is the only one that requires `modality`, so a pago único answer from an API
// before plan 1.4 (without it) still falls through to the order.
export const StoragePaymentStatusSchema = z.union([StorageSubscriptionPaymentStatusSchema, StorageOneTimePaymentStatusSchema]);

// ── Types ──
// Only the inferred types some module reads (knip: `npm run deadcode`). Every schema above stays the
// contract — infer a new type next to these when a screen first needs one.

export type StorageRequestStatus = z.infer<typeof StorageRequestStatusSchema>;
export type StorageRequest = z.infer<typeof StorageRequestSchema>;
export type StorageQuoteDeposit = z.infer<typeof StorageQuoteDepositSchema>;
export type StorageQuote = z.infer<typeof StorageQuoteSchema>;
export type StoragePaymentStatus = z.infer<typeof StoragePaymentStatusSchema>;
export type StorageOneTimePaymentStatus = z.infer<typeof StorageOneTimePaymentStatusSchema>;
export type StorageSubscriptionPaymentStatus = z.infer<typeof StorageSubscriptionPaymentStatusSchema>;
export type StoragePaymentModality = z.infer<typeof StoragePaymentModalitySchema>;
export type StorageGuardadoActionResult = z.infer<typeof StorageGuardadoActionResultSchema>;
export type StorageSubscriptionCancelResult = z.infer<typeof StorageSubscriptionCancelResultSchema>;
export type StorageRenewalResult = z.infer<typeof StorageRenewalResultSchema>;
export type StorageReview = z.infer<typeof StorageReviewSchema>;
export type StorageDepotReviews = z.infer<typeof StorageDepotReviewsSchema>;

export const StorageRequestListSchema = z.array(StorageRequestSchema);

// `GET /storage/requests/:id/quotes`'s `data` shape — the request plus every quote sent to it.
export const StorageRequestQuotesSchema = z.object({
  request: StorageRequestSchema,
  quotes: z.array(StorageQuoteSchema)
});
